/**
 * Modal — wrapper genérico Motion (mesmo padrão de ConfirmModal, sem footer).
 *
 * Usado pra wizards/forms que precisam de espaço maior. Variante "lg" tem
 * max-w-2xl pro BootstrapWizard caber confortavelmente.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { X } from '@phosphor-icons/react';
import { cn } from '@/lib/utils/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Permite fechar via backdrop/ESC. Default true; passe false durante operação crítica. */
  dismissible?: boolean;
  size?: 'md' | 'lg';
  children: ReactNode;
}

/** Seleção de tabbables dentro de um container; cobre o caso comum sem dep externa. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  dismissible = true,
  size = 'md',
  children,
}: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // ESC handler + focus trap + focus restoration.
  // Refs estáveis pra `dismissible`/`onClose` evitam re-rodar o effect
  // (que move foco pro primeiro focusable) toda vez que o pai re-renderiza
  // — isso roubava foco do input ativo durante typing (D-182).
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Sem focus trap, Tab vaza pra controles atrás do modal (e.g. botão Revogar em
  // Grants admin) — a11y e UX foot-gun. Sem restoration, após fechar focus pula
  // pro body, quebrando teclado.
  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    // Move focus pro dialog (pra leitor de tela anunciar + começar trap).
    const dialog = dialogRef.current;
    if (dialog) {
      const first = dialog.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? dialog).focus({ preventScroll: true });
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissibleRef.current) {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !dialog) return;
      // Focus trap: Tab/Shift+Tab cicla dentro do dialog.
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      // Restaurar foco pra origem se ainda estiver no DOM.
      const previous = previousFocusRef.current;
      if (previous && document.contains(previous)) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          {/* Backdrop como div: sem fake button na a11y tree (review M3). */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={dismissible ? onClose : undefined}
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className={cn(
              'relative z-10 flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-elevated outline-none',
              size === 'lg' ? 'max-w-2xl' : 'max-w-md',
            )}
            initial={{ y: 10, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 10, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <header className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 id={titleId} className="text-base font-semibold text-ink">
                {title}
              </h2>
              {dismissible && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Fechar"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-muted hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal"
                >
                  <X size={14} weight="bold" aria-hidden="true" />
                </button>
              )}
            </header>
            <div className="overflow-y-auto p-6">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
